import { Link } from 'react-router-dom';

import styles from './button.module.css';

/**
 * The one button in the app.
 *
 *   <button onClick={save}>Save</button>
 *   <button variant="secondary" size="sm">Cancel</button>
 *   <button variant="danger" loading={busy}>Delete</button>
 *   <button to="/sign-in" fullWidth>Sign in</button>
 *
 * variant: primary | secondary | outline | ghost | danger
 * size:    sm (34px) | md (42px) | lg (48px)
 *
 * `to` renders a react-router <Link>, `href` an <a>; anything else a <button>.
 * Extra class names still win, so a page can nudge one instance without
 * redefining the whole button.
 */
const Button = ({
	variant = 'primary',
	size = 'md',
	fullWidth = false,
	loading = false,
	disabled = false,
	type = 'button',
	to,
	href,
	className = '',
	children,
	...rest
}) => {
	const classes = [
		styles.button,
		styles[variant] || styles.primary,
		styles[size] || styles.md,
		fullWidth ? styles.fullWidth : '',
		loading ? styles.loading : '',
		className,
	]
		.filter(Boolean)
		.join(' ');

	if (to) {
		return (
			<Link to={to} className={classes} {...rest}>
				{children}
			</Link>
		);
	}

	if (href) {
		return (
			<a href={href} className={classes} {...rest}>
				{children}
			</a>
		);
	}

	return (
		<button
			type={type}
			className={classes}
			disabled={disabled || loading}
			aria-busy={loading || undefined}
			{...rest}
		>
			{children}
		</button>
	);
};

export default Button;
